import { gql, useQuery } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import { NavigationStyles } from '@interfaces/role';
import { SwapHoriz } from '@mui/icons-material';
import { ListItemText } from '@mui/material';
import { InlineImage } from '@ovComponents/2-component/inlineImage/inlineImage';
import { UserContext } from '@providers/userContextProvider';
import { Color } from 'hephaestus/core';
import { Box, Skeleton, useTheme } from 'hephaestus/web';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganizationSwitcher } from './organizationSwitcher';

export const FETCH_LOGO = gql`
  query fetchFileDocuments($filter: FileDocumentQueryFilter) {
    fetchFileDocuments(
      input: {
        filter: $filter
        pagination: { perPage: 1, sortDesc: false, sortField: "createdAt" }
      }
    ) {
      fileDocuments {
        id
        name
      }
    }
  }
`;

const LOGO_HEIGHT = '24px';

const OrganizationLogo = ({ textColor }: { textColor: string }) => {
  const {
    activeOrganization,
    userContext,
    organizationLoading,
    organizationFetchFailed,
  } = useContext(UserContext);
  const theme = useTheme();
  const { t } = useTranslation('shared');
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const isUnifiedClientExperience =
    userContext.role?.navigationStyle === NavigationStyles.SIMPLE;

  const { data: logoData, loading: logoLoading } = useQuery(FETCH_LOGO, {
    skip: !activeOrganization.id,
    variables: {
      filter: {
        organizationId: activeOrganization.id,
        types: ['LOGO'],
        objectTypes: ['ORGANIZATION'],
      },
    },
  });
  const documents = logoData?.fetchFileDocuments?.fileDocuments ?? [];

  const logoSkeleton = (
    <Skeleton
      variant='rectangular'
      width={140}
      height={LOGO_HEIGHT}
      sx={{
        bgcolor: Color(textColor).alpha(0.2).string(),
        borderRadius: theme.borderRadius.sm,
      }}
    />
  );

  if ((organizationLoading || logoLoading) && !organizationFetchFailed)
    return logoSkeleton;

  return (
    <>
      <Box
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => !isUnifiedClientExperience && setOpen(true)}
        sx={{
          cursor: !isUnifiedClientExperience ? 'pointer' : 'inherit',
          position: 'relative',
        }}
      >
        {organizationFetchFailed ? (
          <ListItemText
            primary={t('unknownOrganization')}
            sx={{ color: textColor, margin: 0 }}
          />
        ) : documents.length > 0 ? (
          <Box sx={{ height: LOGO_HEIGHT }}>
            <InlineImage
              fileDocumentId={documents[0].id}
              style={{ maxHeight: LOGO_HEIGHT }}
              placeholder={logoSkeleton}
            />
          </Box>
        ) : (
          <ListItemText
            primary={translateBackend(activeOrganization.translatedName)}
            sx={{ color: textColor, margin: 0 }}
          />
        )}
        <SwapHoriz
          sx={{
            color: textColor,
            fontSize: '1.5rem',
            position: 'absolute',
            right: hover ? -30 : -10,
            top: '50%',
            transform: 'translateY(-50%)',
            transition: 'all 0.3s',
            opacity: !isUnifiedClientExperience && hover ? 1 : 0,
          }}
        />
      </Box>
      <OrganizationSwitcher open={open} setOpen={setOpen} />
    </>
  );
};

export default OrganizationLogo;
